import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getXPStatus } from '../api/xp';

const XPContext = createContext(null);

const FRESH_XP_STATUS = {
  level: 0,
  total_xp: 0,
  xp_for_current_level: 0,
  xp_needed_for_next: 100,
  xp_to_level_up: 100,
  level_progress_pct: 0,
};

export function XPProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [xpStatus, setXpStatus] = useState(() => {
    try {
      const cached = sessionStorage.getItem('global_xp_status');
      if (cached) return JSON.parse(cached);
    } catch {}
    return FRESH_XP_STATUS;
  });
  const pollRef = useRef(null);

  const fetchXP = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await getXPStatus();
      // Idempotent: the 60s poll returns a NEW object every time. Storing it
      // unconditionally changes the context value reference each minute, so
      // every useXP() consumer (the XP HUD) re-renders even when nothing
      // changed — and Debono's glassmorphism layers flash one frame on
      // re-render (the "once a minute" flicker, Edward 2026-06-20). Only
      // commit when a field actually changed so React bails out otherwise.
      setXpStatus((prev) => (
        prev
        && prev.level === data.level
        && prev.total_xp === data.total_xp
        && prev.xp_for_current_level === data.xp_for_current_level
        && prev.xp_needed_for_next === data.xp_needed_for_next
        && prev.xp_to_level_up === data.xp_to_level_up
        && prev.level_progress_pct === data.level_progress_pct
      ) ? prev : data);
    } catch (err) {
      // Silently fail — will retry on next poll
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const cached = sessionStorage.getItem('global_xp_status');
    const delay = cached ? 3000 : 0;
    const timeout = setTimeout(() => fetchXP(), delay);
    pollRef.current = setInterval(fetchXP, 60000);
    return () => { clearTimeout(timeout); clearInterval(pollRef.current); };
  }, [fetchXP]);

  const value = useMemo(() => ({
    xpStatus,
    refetchXP: fetchXP,
  }), [xpStatus, fetchXP]);

  return (
    <XPContext.Provider value={value}>
      {children}
    </XPContext.Provider>
  );
}

export function useXP() {
  const context = useContext(XPContext);
  if (!context) {
    throw new Error('useXP must be used within XPProvider');
  }
  return context;
}
